import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const PathwaySelector = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedPath, setSelectedPath] = useState(null);

  const pathwayContent = {
    en: {
      title: "Choose Your Journey",
      subtitle: "Select your role to access tailored features and opportunities",
      brand: {
        title: "For Brands",
        subtitle: "Launch authentic campaigns with verified regional creators",
        features: [
          "Campaign Creation Studio",
          "Creator Discovery Engine",
          "Real-time Analytics",
          "Performance Tracking",
          "ROI Measurement"
        ],
        cta: "Start Campaign",
        description: "Connect with authentic voices that resonate with your target audience"
      },
      creator: {
        title: "For Creators",
        subtitle: "Monetize your content and grow your influence with premium brands",
        features: [
          "Opportunity Marketplace",
          "Content Creation Tools",
          "Earnings Dashboard",
          "Skill Development",
          "Community Support"
        ],
        cta: "Join Platform",
        description: "Transform your creativity into sustainable income with professional tools"
      }
    },
    ar: {
      title: "اختر رحلتك",
      subtitle: "حدد دورك للوصول إلى الميزات والفرص المخصصة",
      brand: {
        title: "للعلامات التجارية",
        subtitle: "أطلق حملات أصيلة مع المبدعين الإقليميين المعتمدين",
        features: [
          "استوديو إنشاء الحملات",
          "محرك اكتشاف المبدعين",
          "التحليلات الفورية",
          "تتبع الأداء",
          "قياس العائد على الاستثمار"
        ],
        cta: "ابدأ الحملة",
        description: "تواصل مع الأصوات الأصيلة التي تتردد مع جمهورك المستهدف"
      },
      creator: {
        title: "للمبدعين",
        subtitle: "استثمر محتواك وانمِ تأثيرك مع العلامات التجارية المميزة",
        features: [
          "سوق الفرص",
          "أدوات إنشاء المحتوى",
          "لوحة الأرباح",
          "تطوير المهارات",
          "دعم المجتمع"
        ],
        cta: "انضم للمنصة",
        description: "حول إبداعك إلى دخل مستدام بأدوات احترافية"
      }
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = pathwayContent?.[currentLanguage];

  return (
    <section className={`py-20 bg-background ${currentLanguage === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
          <h2 className="text-4xl font-bold text-foreground mb-4">
            {content?.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {content?.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Brand Pathway */}
          <div 
            className={`card-elevated p-8 transition-all duration-300 cursor-pointer hover-lift ${
              selectedPath === 'brand' ? 'ring-2 ring-primary' : ''
            }`}
            onMouseEnter={() => setSelectedPath('brand')}
            onMouseLeave={() => setSelectedPath(null)}
          >
            <div className={`space-y-6 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                  <Icon name="Megaphone" size={32} color="white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {content?.brand?.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {content?.brand?.subtitle}
                  </p>
                </div>
              </div>

              <div className="relative h-48 rounded-lg overflow-hidden">
                <Image
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop"
                  alt="Brand campaign management dashboard"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-sm font-medium">Campaign Management</p>
                </div>
              </div>

              <p className="text-muted-foreground">
                {content?.brand?.description}
              </p>

              <div className="space-y-3">
               
                <ul className="space-y-2">
                  {content?.brand?.features?.map((feature, index) => (
                    <li key={index} className={`flex items-right space-x-3`}>
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link to="/brand-campaign-studio" className="block">
                <Button 
                  variant="default" 
                  size="lg" 
                  className="w-full"
                  iconName="ArrowRight"
                  iconPosition={currentLanguage === 'ar' ? 'left' : 'right'}
                >
                  {content?.brand?.cta}
                </Button>
              </Link>
            </div>
          </div>

          {/* Creator Pathway */}
          <div 
            className={`card-elevated p-8 transition-all duration-300 cursor-pointer hover-lift ${
              selectedPath === 'creator' ? 'ring-2 ring-accent' : ''
            }`}
            onMouseEnter={() => setSelectedPath('creator')}
            onMouseLeave={() => setSelectedPath(null)}
          >
            <div className={`space-y-6 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center">
                  <Icon name="Users" size={32} color="white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {content?.creator?.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {content?.creator?.subtitle}
                  </p>
                </div>
              </div>

              <div className="relative h-48 rounded-lg overflow-hidden">
                <Image
                  src="https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop"
                  alt="Creator content creation workspace"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-sm font-medium">Content Creation</p>
                </div>
              </div>

              <p className="text-muted-foreground">
                {content?.creator?.description}
              </p>

              <div className="space-y-3">
              
                <ul className="space-y-2">
                  {content?.creator?.features?.map((feature, index) => (
                    <li key={index} className={`flex items-center space-x-3`}>
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link to="/creator-dashboard" className="block">
                <Button 
                  variant="default" 
                  size="lg" 
                  className="w-full bg-accent hover:bg-accent/90"
                  iconName="ArrowRight"
                  iconPosition={currentLanguage === 'ar' ? 'left' : 'right'}
                >
                  {content?.creator?.cta}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PathwaySelector;